import { isDefined } from "@clipboard-health/util-ts";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import { type AxiosError, type CustomParamsSerializer } from "axios";
import type { z } from "zod";

import { get, type ZodSchema } from "./parsedApi";
import { type ReactQueryMeta } from "./types";

type LocallyDefinedUseQueryOptions = "meta" | "queryKey" | "queryFn";
type DisallowedUseQueryOptions = "onSuccess" | "onError" | "onSettled";

export type UseGetParsedQueryOptions<
  ResponseSchema extends ZodSchema,
  SelectData = z.output<ResponseSchema>
> = Omit<
  UseQueryOptions<z.output<ResponseSchema>, AxiosError, SelectData, [string, unknown]>,
  LocallyDefinedUseQueryOptions | DisallowedUseQueryOptions
>;

interface BaseProps<ResponseSchema extends ZodSchema> {
  url: string;
  responseSchema: ResponseSchema;
  customHeaders?: Record<string, string>;
  paramsSerializer?: CustomParamsSerializer;
  meta: ReactQueryMeta;
}

type PropsWithQueryParams<
  ResponseSchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema,
  SelectData = z.output<ResponseSchema>
> = BaseProps<ResponseSchema> &
  UseGetParsedQueryOptions<ResponseSchema, SelectData> & {
    queryParamsSchema: QueryParamsSchema;
    queryParams: z.input<QueryParamsSchema>;
  };

type PropsWithoutQueryParams<
  ResponseSchema extends ZodSchema,
  SelectData = z.output<ResponseSchema>
> = BaseProps<ResponseSchema> &
  UseGetParsedQueryOptions<ResponseSchema, SelectData> & {
    queryParamsSchema?: undefined;
    queryParams?: undefined;
  };

export type UseGetParsedQueryProps<
  ResponseSchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
  SelectData = z.output<ResponseSchema>
> = QueryParamsSchema extends ZodSchema
  ? PropsWithQueryParams<ResponseSchema, QueryParamsSchema, SelectData>
  : PropsWithoutQueryParams<ResponseSchema, SelectData>;

export function useGetParsedQuery<
  ResponseSchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
  SelectData = z.output<ResponseSchema>
>(
  props: UseGetParsedQueryProps<ResponseSchema, QueryParamsSchema, SelectData>
): UseQueryResult<SelectData> {
  const {
    url,
    queryParams,
    queryParamsSchema,
    responseSchema,
    customHeaders,
    paramsSerializer,
    ...options
  } = props;

  return useQuery({
    ...options,
    queryKey: [url, queryParams],
    queryFn: async () => {
      if (isDefined(queryParamsSchema)) {
        const response = await get({
          url,
          queryParams,
          queryParamsSchema,
          responseSchema,
          customHeaders,
          paramsSerializer,
        });
        return response.data;
      }

      const response = await get({
        url,
        responseSchema,
        customHeaders,
        paramsSerializer,
      });
      return response.data;
    },
  });
}
